/**
 * Typed Firestore access helpers.
 *
 * Centralises the per-user collection/document references used by the feature
 * hooks and routes, so paths and `@agente/shared` document types stay in one
 * place. Everything is keyed by the Firebase `uid` (multi-user).
 *
 * Timestamps are stored as epoch milliseconds (number) — never Firestore
 * `Timestamp` — so writes use `Date.now()` and reads use the numbers as-is.
 */
import {
  type ClientProfileDoc,
  COLLECTIONS,
  type ConversationDoc,
  type EmailResearchJobDoc,
  type FolderDoc,
  type MemoryDoc,
  type MemoryStatus,
  type MessageDoc,
  type MessagePart,
  type ReminderDoc,
  type ReminderStatus,
  type UserDoc,
  type UserPreferences,
} from '@agente/shared'
import {
  addDoc,
  type CollectionReference,
  collection,
  type DocumentReference,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore'

import { db } from './firebase'

/** users/{uid} */
export function userDoc(uid: string): DocumentReference<UserDoc> {
  return doc(db, COLLECTIONS.users, uid) as DocumentReference<UserDoc>
}

/** users/{uid}/conversations */
export function conversationsCol(uid: string): CollectionReference<ConversationDoc> {
  return collection(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.conversations,
  ) as CollectionReference<ConversationDoc>
}

/** users/{uid}/conversations/{conversationId} */
export function conversationDoc(
  uid: string,
  conversationId: string,
): DocumentReference<ConversationDoc> {
  return doc(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.conversations,
    conversationId,
  ) as DocumentReference<ConversationDoc>
}

/** users/{uid}/conversations/{conversationId}/messages */
export function messagesCol(uid: string, conversationId: string): CollectionReference<MessageDoc> {
  return collection(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.conversations,
    conversationId,
    COLLECTIONS.messages,
  ) as CollectionReference<MessageDoc>
}

/** users/{uid}/folders */
export function foldersCol(uid: string): CollectionReference<FolderDoc> {
  return collection(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.folders,
  ) as CollectionReference<FolderDoc>
}

/** users/{uid}/folders/{folderId} */
export function folderDoc(uid: string, folderId: string): DocumentReference<FolderDoc> {
  return doc(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.folders,
    folderId,
  ) as DocumentReference<FolderDoc>
}

/** users/{uid}/reminders */
export function remindersCol(uid: string): CollectionReference<ReminderDoc> {
  return collection(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.reminders,
  ) as CollectionReference<ReminderDoc>
}

/** users/{uid}/reminders/{reminderId} */
export function reminderDoc(uid: string, reminderId: string): DocumentReference<ReminderDoc> {
  return doc(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.reminders,
    reminderId,
  ) as DocumentReference<ReminderDoc>
}

/** users/{uid}/clients */
export function clientsCol(uid: string): CollectionReference<ClientProfileDoc> {
  return collection(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.clients,
  ) as CollectionReference<ClientProfileDoc>
}

/** users/{uid}/clients/{clientId} */
export function clientDoc(uid: string, clientId: string): DocumentReference<ClientProfileDoc> {
  return doc(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.clients,
    clientId,
  ) as DocumentReference<ClientProfileDoc>
}

/** users/{uid}/memories */
export function memoriesCol(uid: string): CollectionReference<MemoryDoc> {
  return collection(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.memories,
  ) as CollectionReference<MemoryDoc>
}

/** users/{uid}/memories/{memoryId} */
export function memoryDoc(uid: string, memoryId: string): DocumentReference<MemoryDoc> {
  return doc(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.memories,
    memoryId,
  ) as DocumentReference<MemoryDoc>
}

/** users/{uid}/emailResearch/{jobId} — somente leitura pelo cliente (rules). */
export function emailResearchDoc(
  uid: string,
  jobId: string,
): DocumentReference<EmailResearchJobDoc> {
  return doc(
    db,
    COLLECTIONS.users,
    uid,
    COLLECTIONS.emailResearch,
    jobId,
  ) as DocumentReference<EmailResearchJobDoc>
}

// ---------------------------------------------------------------------------
// Write helpers
//
// Timestamps are epoch milliseconds (`Date.now()`), never Firestore Timestamps.
// Preference/profile edits are shallow merges so a partial write never clobbers
// sibling fields the backend also maintains (e.g. `microsoft` on the user doc).
// ---------------------------------------------------------------------------

/**
 * Merge-update the signed-in user's `preferences`. Accepts a partial so a single
 * toggle can be persisted without re-sending the whole object; nested
 * `quietHours`/`routines` should be passed whole since Firestore merges only at
 * the field level, not inside map values.
 */
export function updateUserPreferences(
  uid: string,
  preferences: Partial<UserPreferences>,
): Promise<void> {
  return setDoc(userDoc(uid), { preferences, updatedAt: Date.now() }, { merge: true })
}

/** Fields the user can edit on a client profile (the rest are managed on write). */
export type ClientProfileInput = Pick<
  ClientProfileDoc,
  'name' | 'company' | 'sector' | 'notes' | 'matchKeywords'
>

/** Create a new client profile under the signed-in user. Returns the new id. */
export async function createClient(uid: string, input: ClientProfileInput): Promise<string> {
  const now = Date.now()
  const ref = await addDoc(clientsCol(uid), {
    ...input,
    createdAt: now,
    updatedAt: now,
  } as ClientProfileDoc)
  return ref.id
}

/** Update an existing client profile. */
export function updateClient(
  uid: string,
  clientId: string,
  input: ClientProfileInput,
): Promise<void> {
  return updateDoc(clientDoc(uid, clientId), { ...input, updatedAt: Date.now() })
}

/** Permanently delete a client profile. */
export function deleteClient(uid: string, clientId: string): Promise<void> {
  return deleteDoc(clientDoc(uid, clientId))
}

/**
 * Cria uma pasta de conversas. O id é gerado localmente (`doc()`) e gravado no
 * próprio doc, seguindo o padrão dos helpers de escrita. Retorna o novo id.
 */
export async function createFolder(uid: string, name: string): Promise<string> {
  const ref = doc(foldersCol(uid))
  const now = Date.now()
  await setDoc(ref, { id: ref.id, name, createdAt: now, updatedAt: now } satisfies FolderDoc)
  return ref.id
}

/** Renomeia uma pasta. */
export function renameFolder(uid: string, folderId: string, name: string): Promise<void> {
  return updateDoc(folderDoc(uid, folderId), { name, updatedAt: Date.now() })
}

/**
 * Exclui a pasta (hard delete — pasta é organizacional, não histórico). Antes de
 * apagar o doc da pasta, reatribui `folderId: null` a todas as conversas NÃO
 * excluídas daquela pasta num único batch, para que voltem a "Sem pasta" em vez
 * de sumirem. Conversas já excluídas (soft delete) não são tocadas.
 */
export async function deleteFolder(uid: string, folderId: string): Promise<void> {
  const snap = await getDocs(query(conversationsCol(uid), where('folderId', '==', folderId)))
  const batch = writeBatch(db)
  // Limite do Firestore: um writeBatch aceita no máximo 500 escritas. Com >499
  // conversas na pasta (reatribuições + o delete da pasta), o commit lança e cai
  // no toast de erro — sem corromper nada, pois o batch é atômico (tudo ou nada).
  // Chunking em vários batches fica para quando esse volume for plausível.
  const now = Date.now()
  for (const d of snap.docs) {
    if (d.data().deletedAt != null) continue
    batch.update(d.ref, { folderId: null, updatedAt: now })
  }
  batch.delete(folderDoc(uid, folderId))
  await batch.commit()
}

/**
 * Exclusão SUAVE de uma conversa: marca `deletedAt` para que suma da lista do
 * usuário, mantendo o doc e as mensagens como histórico. NUNCA faz hard delete.
 */
export function softDeleteConversation(uid: string, conversationId: string): Promise<void> {
  const now = Date.now()
  return updateDoc(conversationDoc(uid, conversationId), { deletedAt: now, updatedAt: now })
}

/**
 * Persiste as ferramentas DESABILITADAS de uma conversa (ver AGENT_TOOL_CATALOG).
 * O backend relê o campo a cada turno, então a mudança vale para as próximas
 * mensagens/sessões daquela conversa. Lista vazia ⇒ todas habilitadas.
 */
export function setConversationDisabledTools(
  uid: string,
  conversationId: string,
  disabledTools: string[],
): Promise<void> {
  return updateDoc(conversationDoc(uid, conversationId), {
    disabledTools,
    updatedAt: Date.now(),
  })
}

/** Move uma conversa para uma pasta (ou para "Sem pasta" com `folderId: null`). */
export function setConversationFolder(
  uid: string,
  conversationId: string,
  folderId: string | null,
): Promise<void> {
  return updateDoc(conversationDoc(uid, conversationId), { folderId, updatedAt: Date.now() })
}

/** Change a reminder's status (e.g. mark done or cancel). */
export function updateReminderStatus(
  uid: string,
  reminderId: string,
  status: ReminderStatus,
): Promise<void> {
  return updateDoc(reminderDoc(uid, reminderId), { status, updatedAt: Date.now() })
}

/** Firestore hard cap on writes per `writeBatch().commit()`. */
const WRITE_BATCH_LIMIT = 500

export interface BatchStatusResult {
  succeeded: number
  failed: number
}

/**
 * Change the status of many reminders at once (e.g. "aceitar todos" /
 * "descartar todos" quando os sugeridos se acumulam). Each chunk of up to
 * `WRITE_BATCH_LIMIT` ids is committed as one atomic batch — mirrors
 * `updateReminderStatus` field-for-field, just paginated so a pile-up beyond
 * 500 items doesn't blow past the Firestore batch limit (see `deleteFolder`
 * for the same pattern). A chunk failing (e.g. offline) doesn't stop the
 * remaining chunks; the caller gets a succeeded/failed tally instead of a
 * thrown error, so the UI can report partial failure without getting stuck.
 */
export async function updateReminderStatusBatch(
  uid: string,
  reminderIds: string[],
  status: ReminderStatus,
): Promise<BatchStatusResult> {
  const now = Date.now()
  let succeeded = 0
  let failed = 0
  for (let i = 0; i < reminderIds.length; i += WRITE_BATCH_LIMIT) {
    const chunk = reminderIds.slice(i, i + WRITE_BATCH_LIMIT)
    const batch = writeBatch(db)
    for (const id of chunk) {
      batch.update(reminderDoc(uid, id), { status, updatedAt: now })
    }
    try {
      await batch.commit()
      succeeded += chunk.length
    } catch {
      failed += chunk.length
    }
  }
  return { succeeded, failed }
}

/**
 * Curadoria de memórias. Criação é só do backend (regras negam `create` ao
 * cliente); a UI apenas aprova/rejeita, edita o conteúdo e exclui. Aprovar uma
 * memória rejeitada (`status: 'approved'`) a reativa no contexto do agente.
 */
export function updateMemoryStatus(
  uid: string,
  memoryId: string,
  status: MemoryStatus,
): Promise<void> {
  return updateDoc(memoryDoc(uid, memoryId), { status, updatedAt: Date.now() })
}

/** Fields the user can edit on a memory (o restante é gerido na escrita do backend). */
export function updateMemoryContent(
  uid: string,
  memoryId: string,
  patch: Pick<MemoryDoc, 'content' | 'subject' | 'category'>,
): Promise<void> {
  return updateDoc(memoryDoc(uid, memoryId), { ...patch, updatedAt: Date.now() })
}

/** Permanently delete a memory. */
export function deleteMemory(uid: string, memoryId: string): Promise<void> {
  return deleteDoc(memoryDoc(uid, memoryId))
}

/**
 * Marca o `confirmationStatus` de um part de confirmação (a trava de escritas) na
 * mensagem do assistant. O Firestore não atualiza um item de array isoladamente,
 * então lê o doc, ajusta apenas o part no índice informado e regrava o array
 * `parts` inteiro (leitura-modificação-escrita — mesmo padrão da persistência de
 * voz). A mensagem é do próprio dono, e as regras permitem o update.
 */
export async function updateMessagePartConfirmation(
  uid: string,
  conversationId: string,
  messageId: string,
  partIndex: number,
  status: NonNullable<MessagePart['confirmationStatus']>,
): Promise<void> {
  const ref = doc(messagesCol(uid, conversationId), messageId)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Mensagem não encontrada.')
  const parts = snap.data().parts ?? []
  const target = parts[partIndex]
  if (target?.type !== 'confirmation') {
    throw new Error('Confirmação não encontrada na mensagem.')
  }
  const nextParts = parts.map((part, index) =>
    index === partIndex ? { ...part, confirmationStatus: status } : part,
  )
  await updateDoc(ref, { parts: nextParts, updatedAt: Date.now() })
}

/**
 * Ao cancelar uma confirmação pelo APP, também limpa a
 * `ConversationDoc.whatsappPendingConfirmation` da conversa quando ela aponta
 * para este mesmo cartão (messageId + partIndex) — sem isso, um "sim" tardio
 * pelo WhatsApp ainda executaria a ação já cancelada no app. Lê o doc da
 * conversa, compara e só grava em caso de match; não faz nada se a conversa
 * não tiver pendência ou se a pendência for de outro cartão.
 */
export async function clearWhatsappPendingConfirmationIfMatches(
  uid: string,
  conversationId: string,
  messageId: string,
  partIndex: number,
): Promise<void> {
  const ref = conversationDoc(uid, conversationId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return
  const pending = snap.data().whatsappPendingConfirmation
  if (!pending || pending.messageId !== messageId || pending.partIndex !== partIndex) return
  await updateDoc(ref, { whatsappPendingConfirmation: null, updatedAt: Date.now() })
}
